import { useId, useState } from 'react'

/**
 * ช่องรหัสผ่าน + ปุ่มรูปดวงตาสำหรับดูสิ่งที่พิมพ์
 *
 * ── ทำไมต้องมีให้ดูได้ ──────────────────────────────────────────────────────
 * รหัสผ่านของระบบนี้ถูก **ส่งต่อด้วยการบอกกันปากเปล่าหรือพิมพ์ในแชท** (ผู้ดูแลสร้าง
 * บัญชีแล้วให้รหัสชั่วคราวไปเปลี่ยนเอง) คนที่พิมพ์ตามจึงพิมพ์ผิดได้ง่าย และช่องที่
 * ปิดบังไว้ทำให้ไม่รู้ว่าผิดจนกดส่งแล้วโดนปฏิเสธ — เสียเวลาและทำให้ถูกล็อกบัญชี
 * เพราะกรอกผิดครบ 5 ครั้งโดยไม่ได้ตั้งใจ
 *
 * ── ตั้งใจให้ปิดไว้ก่อน ─────────────────────────────────────────────────────
 * เริ่มที่ซ่อนเสมอ ต้องกดเองถึงจะเห็น — ค่าเริ่มต้นที่เปิดจะทำให้รหัสโผล่บนจอ
 * ตอนมีคนเดินผ่านหรือตอนแชร์หน้าจอโดยไม่มีใครทันคิด
 *
 * ⚠️ `type` สลับระหว่าง password/text ไม่ได้แปลว่าค่าปลอดภัยขึ้นหรือน้อยลง —
 *    ค่าอยู่ใน DOM เหมือนกันทั้งสองแบบ ตัวปิดบังมีผลกับ "สายตาคนข้าง ๆ" เท่านั้น
 *
 * ⚠️ ต้องส่ง `autoComplete` มาเองทุกครั้ง — 'current-password' ตอนล็อกอิน
 *    กับ 'new-password' ตอนตั้งรหัสใหม่ ใส่ผิดแล้วเบราว์เซอร์จะเติมรหัสเก่าลงช่อง
 *    รหัสใหม่ให้เอง แล้วผู้ใช้กดบันทึกทับโดยไม่ได้ตั้งใจ
 */
export function PasswordInput({
  value,
  onChange,
  autoComplete,
  required,
  minLength,
  autoFocus,
  id,
}: {
  value: string
  onChange: (value: string) => void
  autoComplete: 'current-password' | 'new-password'
  required?: boolean
  minLength?: number
  autoFocus?: boolean
  id?: string
}) {
  const [shown, setShown] = useState(false)
  const fallbackId = useId()
  const inputId = id ?? fallbackId

  return (
    <span className="pw">
      <input
        id={inputId}
        className="pw__input"
        type={shown ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required={required}
        minLength={minLength}
        autoFocus={autoFocus}
      />
      <button
        type="button"
        className="pw__eye"
        onClick={() => setShown((v) => !v)}
        /* aria-pressed บอก screen reader ว่าปุ่มนี้เป็นสวิตช์ที่กดค้างอยู่หรือไม่
           ส่วน aria-controls ผูกปุ่มกับช่องที่มันคุม (มีหลายช่องในหน้าเดียว) */
        aria-pressed={shown}
        aria-controls={inputId}
        aria-label={shown ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
        title={shown ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
      >
        <Eye off={shown} />
      </button>
    </span>
  )
}

/**
 * ดวงตา — `off` = กำลังแสดงรหัสอยู่ จึงวาดตาที่มีขีดขวาง (กดแล้วจะซ่อน)
 *
 * ไอคอนบอก "ผลของการกด" ไม่ใช่ "สถานะตอนนี้" ตามที่คนคุ้นจากแอปอื่น —
 * ตอนรหัสถูกซ่อน เห็นตาเปล่า = กดเพื่อดู
 */
function Eye({ off }: { off: boolean }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {/* เปลือกตาเป็นรูปเลนส์ — โค้ง C สองเส้นประกบกัน (โค้ง S ที่เขียนไว้ตอนแรก
          ออกมาแบนจนดูไม่ออกว่าเป็นตา) */}
      <path d="M10 4.6c-4.4 0-7.6 5.4-7.6 5.4s3.2 5.4 7.6 5.4 7.6-5.4 7.6-5.4-3.2-5.4-7.6-5.4Z" />
      <circle cx="10" cy="10" r="2.4" />
      {/* ขีดขวางต้องพาดทั้งไอคอนถึงจะเห็นในขนาด ~18px และต้องมีเส้นขอบสีพื้น
          คั่นไว้ ไม่งั้นมันจะกลืนไปกับเปลือกตาตรงจุดที่ตัดกัน */}
      {off && (
        <>
          <path d="M3.2 16.8 16.8 3.2" stroke="var(--surface)" strokeWidth="3.2" />
          <path d="M3.2 16.8 16.8 3.2" />
        </>
      )}
    </svg>
  )
}
